"use client";

import React from "react";

import { ResultsCard } from "@/components/common/ResultsCard/ResultsCard";
import { Results } from "@/components/common/ResultsSlider/ResultsRoot";
import Section from "@/components/common/Section/Section";
import { ButtonNova } from "@/components/ui-kit/ButtonNova/ButtonNova";
import { TextElementNova } from "@/components/ui-kit/TextElementNova/TextElementNova";
import { ResultsSectionProps } from "@/types/results";
import { cn } from "@/utils/general";

const ResultsSection9 = ({ data }: ResultsSectionProps) => {
  const footerDisclaimer = data?.settings?.footerDisclaimer;
  return (
    <Section>
      <Results slidesCount={data.slides.length} autoplay={true}>
        <div className="mb-8 md:mb-10 lg:mb-20">
          <TextElementNova
            element={data.heading.element || "h2"}
            text={data.heading.text}
            className={cn(
              "Title-20-Super-Normal mb-8 lg:mb-10 text-center mx-auto",
              data.heading.className
            )}
          />
          <Results.ThumbnailSlider>
            {data.slides.map((slide) => (
              <Results.Thumbnail
                key={slide.name}
                thumbnail={slide.icon || slide.beforeImage}
                name={slide.name}
              />
            ))}
          </Results.ThumbnailSlider>
        </div>
        <Results.SliderEmbla gap={8} slideClassName="basis-full" className="touch-pan-y">
          {data.slides.map((slide) => (
            <ResultsCard
              key={slide.name}
              variant="inline"
              className="max-w-[910px] mx-auto xl:max-w-none"
            >
              <ResultsCard.ImageGroup>
                <ResultsCard.Image
                  src={slide.beforeImage}
                  tag="Before"
                  alt={`${slide.name} before taking ${slide.product}.`}
                  imageDirection={slide.imageDirection as "center" | "left" | "right"}
                  className="[-webkit-user-drag:none]"
                />
                <ResultsCard.Image
                  src={slide.afterImage}
                  tag={`${slide.after} months growing`}
                  alt={`${slide.name} ${slide.after} months into taking ${slide.product}. Results are from a clinical study and may vary`}
                  imageDirection={slide.imageDirection as "center" | "left" | "right"}
                  className="[-webkit-user-drag:none]"
                />
              </ResultsCard.ImageGroup>

              <ResultsCard.Content className="p-4 md:p-6 lg:p-6 xl:p-4 2xl:p-8">
                {slide.quote ? (
                  <TextElementNova
                    element="h3"
                    text={`“${slide.quote}”`}
                    className={
                      "Subtitle-10-Dia-Normal mb-4 md:mb-6 md:!text-[22px] lg:mb-4 2xl:!text-[28px]"
                    }
                  />
                ) : null}

                <TextElementNova
                  element="h3"
                  text={slide.name}
                  className={"Caption-10-Dia-Uppercase mb-10 md:mb-8 lg:mb-10"}
                />
                <ResultsCard.Tags tags={slide.tags} className="mb-10 md:mb-12 lg:mb-10" />
                <ResultsCard.Product
                  product={slide.product}
                  action={slide.action}
                  pdp={slide.pdp}
                  className="mt-auto"
                />
                {slide.disclaimerText ? (
                  <TextElementNova
                    text={slide.disclaimerText}
                    className="Caption-10-Dia-Normal pt-2 text-nova-transparency-black-70"
                  />
                ) : null}
              </ResultsCard.Content>
            </ResultsCard>
          ))}
        </Results.SliderEmbla>
        {data.cta && (
          <div className="w-full mt-8 md:mt-10 lg:mt-20">
            <ButtonNova
              label={data.cta.label}
              icon={"Left"}
              level={"Tertiary"}
              ctaLink={data.cta.url}
              trackObject={data.cta.trackObject}
            />
          </div>
        )}
        {footerDisclaimer ? (
          <div className={`pt-4 text-center`}>
            <TextElementNova
              element="span"
              className={`Caption-10-Dia-Normal text-nova-transparency-black-70 block`}
              text={footerDisclaimer}
            />
          </div>
        ) : null}
      </Results>
    </Section>
  );
};

export default ResultsSection9;
